import React from "react";
import styled from "styled-components";
import { RotateCcw, Save } from "react-feather";
import {
  Banner,
  Button as ButtonComponent,
  Section,
  TreeRow,
  TreeView,
} from "@cinesys/ui/components";
import { SPOTTER_STRINGS } from "./strings";
import SpotterEditedTag from "./SpotterEditedTag";
import SpotterNewTag from "./SpotterNewTag";
import SpotterAddedTag from "./SpotterAddedTag";
import SpotterRemovedTag from "./SpotterRemovedTag";

const STATUS_TAGS = {
  added: SpotterAddedTag,
  removed: SpotterRemovedTag,
  edited: SpotterEditedTag,
};

/**
 * "Unsaved changes" card for a Spotter settings screen (Field Mapping, Glossary, Rename) — same
 * `Section`-based shape as Vault's own `ChangesPanel`
 * (`ab-vault/candidate-assignments/CandidateAssignmentsScreen.tsx`): title + a `TreeView` of what
 * changed, Cancel/Save in the `footer`. Reused as an APPROACH, not the same component instance,
 * since Vault's is built around its add/remove-candidate tree model and this one groups by
 * view/field instead (`groups: SpotterChangeGroup[]`).
 *
 * Renders with `Section`'s own default chrome (no accent-colored border for pending changes) —
 * matches the other `Section` cards already sitting in the same `<Aside>` column
 * (`FieldsScreen.tsx`'s Tag Preview / Unused Sources cards use plain `Section` too), and is what
 * `SpotterHeaderChanges` renders unmodified inside its own dropdown on narrow viewports.
 *
 * Deliberately no unsaved-count badge in the title bar — `SpotterHeader` already shows its own
 * "N unsaved changes" pill in the tab row (`SpotterHeaderChanges`, passed this panel's same
 * props), so repeating the count here would duplicate that text right next to it.
 */
const GroupLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--spotterInk);

  > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ItemLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 13px;
  color: var(--spotterInkSoft);

  > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const TREE_COLS = [{ width: "1fr" }];

/** One changed view/field, with the individual changes under it (e.g. group "Camera Angle" with
 *  items "wide → Wide Shot", "tight → Close Up"). The New/Edited tag is shown once, on the group
 *  itself — a field with several distinct edits underneath is still one add-or-edit, not several.
 *  Each item can carry its own `status` pill instead — "added"/"removed" for one value gained or
 *  lost from a list (a source, a term, a routed person), "edited" for a change that isn't a plain
 *  add/remove. `badge` overrides that status pill entirely, for a caller with its own icon/label
 *  to show on that specific item instead of a generic pill. */
export interface SpotterChangeGroup {
  id: string;
  label: React.ReactNode;
  isNew?: boolean;
  items: {
    id: string;
    label: React.ReactNode;
    status?: "added" | "removed" | "edited";
    badge?: React.ReactNode;
  }[];
}

export interface SpotterChangesPanelProps {
  groups: SpotterChangeGroup[];
  onSave: () => void;
  onCancel: () => void;
  isSaving?: boolean;
  saveError?: string | null;
  /** Shown once, right after a successful save, while there are no pending changes. */
  saveSuccess?: boolean;
  /** A warning shown beside the "Saved" confirmation, e.g. rows that had nothing to save. */
  saveNotice?: string | null;
  /** When set, Save is disabled and this renders in place of `saveError` — for a required field
   *  that's still unset (e.g. Rename's Original Name Field). Re-evaluated on every render, so it
   *  clears itself the moment the caller's own validation passes — no separate "dismiss" needed. */
  saveDisabledReason?: string | null;
  /** Optional shortcut rendered under `saveDisabledReason`, e.g. jumping to the row that needs
   *  fixing when it isn't on screen. */
  saveDisabledAction?: { label: string; onClick: () => void };
}

const SpotterChangesPanel = ({
  groups,
  onSave,
  onCancel,
  isSaving,
  saveError,
  saveSuccess,
  saveNotice,
  saveDisabledReason,
  saveDisabledAction,
}: SpotterChangesPanelProps) => {
  const count = groups.reduce((n, g) => n + g.items.length, 0);
  const treeRows: TreeRow[] = groups.flatMap((g) => [
    {
      id: `group:${g.id}`,
      depth: 0,
      hasChildren: true,
      firstElement: (
        <GroupLabel>
          <span>{g.label}</span>
          {g.isNew ? <SpotterNewTag /> : <SpotterEditedTag />}
        </GroupLabel>
      ),
    },
    ...g.items.map((item) => {
      const StatusTag = item.status && STATUS_TAGS[item.status];
      return {
        id: `item:${g.id}:${item.id}`,
        depth: 1,
        parentId: `group:${g.id}`,
        firstElement: (
          <ItemLabel>
            <span>{item.label}</span>
            {item.badge ?? (StatusTag && <StatusTag />)}
          </ItemLabel>
        ),
      };
    }),
  ]);

  return (
    <Section
      title={SPOTTER_STRINGS.changesPanel.title}
      footer={
        count > 0 ? (
          <>
            <ButtonComponent
              label={SPOTTER_STRINGS.changesPanel.cancel}
              icon={RotateCcw}
              type="danger"
              borderless
              onClick={onCancel}
              disabled={isSaving}
            />
            <ButtonComponent
              label={
                isSaving
                  ? SPOTTER_STRINGS.changesPanel.saving
                  : SPOTTER_STRINGS.changesPanel.save
              }
              icon={Save}
              type="success"
              onClick={onSave}
              loading={isSaving}
              disabled={isSaving || !!saveDisabledReason}
            />
          </>
        ) : undefined
      }
    >
      {saveDisabledReason ? (
        <>
          <Banner error={saveDisabledReason} padBottom />
          {saveDisabledAction && (
            <ButtonComponent
              label={saveDisabledAction.label}
              type="default"
              borderless
              onClick={saveDisabledAction.onClick}
            />
          )}
        </>
      ) : (
        saveError && <Banner error={saveError} padBottom />
      )}
      {saveSuccess && count === 0 && (
        <Banner success={SPOTTER_STRINGS.changesPanel.saved} padTop />
      )}
      {saveSuccess && count === 0 && saveNotice && (
        <Banner warn={saveNotice} padTop />
      )}
      {count === 0 ? (
        <Banner
          placeholder={SPOTTER_STRINGS.changesPanel.noPendingChanges}
          padTop
          padBottom
        />
      ) : (
        <TreeView
          columns={TREE_COLS}
          rows={treeRows}
          rowHeight={32}
          buttonSize={30}
          rowPadding="2.5px 10px"
        />
      )}
    </Section>
  );
};

export default SpotterChangesPanel;
